/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { InjectedFormProps, reduxForm, getFormValues } from 'redux-form';
import { AxiosError } from 'axios';
import { useRootSelector } from '../../../hooks/useRootSelector';

import { State } from '../../../redux';
import { updateExitModalStatus as updateExitModalStatusAction } from '../../../actions/signingRoomActions';
import { TaskForm } from '../../form/TaskForm';
import { TaskInputs } from '../../form/TaskInputs/TaskInputs';
import { InputConfig, InputType } from '../../form/TaskInputs/types';
import {
  CallClosedReason,
  CallClosedReasonLabels,
  CallClosedReasonLabelsV1,
  CallClosedReasonV1,
} from '../../../types/call';
import { ConfirmationModal } from '../../common/Modals/ConfirmationModal/ConfirmationModal';
import { notaryAPI } from '../../../api/notary';
import { NotificationItem } from '../../common/Notifications/NotificationItem';
import { addNotifications as addNotificationsAction } from '../../../actions/pageActions';
import { useDocuments } from '../../../pages/SigningRoomPage/hooks/useDocuments';
import { getConfig } from '../../../utils/configs';
import { goTo } from '@utils/routing';
import { diagnostics } from '@utils/diagnostics';
import { getFilteredInputs } from '@utils/formUtils';
import { noop } from '@utils/lodash';

const FORM_NAME = 'notary_exit_modal';
const INPUT_CONFIGS = [
  {
    id: 'isCallCompleted',
    type: 'radio' as InputType,
    required: true,
    choices: [
      { text: 'All documents have been signed and completed', value: true },
      { text: 'Document signing could not be completed', value: false },
    ],
  },
  {
    id: 'callClosedReason',
    type: 'dropdown' as InputType,
    required: true,
    choices: Object.keys(CallClosedReasonLabels).map(reasonKey => ({
      text: CallClosedReasonLabels[reasonKey as CallClosedReason],
      value: reasonKey,
    })),
    // display dropdown input to select incomplete reason when the notary selects signing is incomplete
    showCondition: {
      property: 'isCallCompleted',
      value: false,
    },
  },
];

const INPUT_CONFIGS_V1 = [
  {
    id: 'isCallCompleted',
    type: 'radio' as InputType,
    required: true,
    choices: [
      { text: 'All documents have been signed and completed', value: true },
      { text: 'Document signing could not be completed', value: false },
    ],
  },
  {
    id: 'callClosedReason',
    type: 'dropdown' as InputType,
    required: true,
    choices: Object.keys(CallClosedReasonLabelsV1).map(reasonKey => ({
      text: CallClosedReasonLabelsV1[reasonKey as CallClosedReasonV1],
      value: reasonKey,
    })),
    // display dropdown input to select incomplete reason when the notary selects signing is incomplete
    showCondition: {
      property: 'isCallCompleted',
      value: false,
    },
  },
  {
    id: 'callNeedsReschedule',
    label: 'Will this appointment be rescheduled for later today?',
    type: 'radio' as InputType,
    required: true,
    choices: [
      { text: 'Yes', value: true },
      { text: 'No', value: false },
    ],
    // display additional question to check if reschedule is needed when notary selects signing is incomplete
    showCondition: {
      property: 'isCallCompleted',
      value: false,
    },
  },
  {
    id: 'comments',
    label: 'Comments',
    type: 'longText' as InputType,
    required: false,
    showCondition: {
      property: 'isCallCompleted',
      value: false,
    },
  },
];

const shouldDeleteDocsInput = {
  id: 'shouldDeleteDocs',
  type: 'boolean' as InputType,
  required: false,
  displayParts: [`Delete document package upon ending this appointment.`],
  showCondition: {
    property: 'isCallCompleted',
    value: false,
  },
};

export namespace ExitSessionConfirmationModal {
  export interface FormValues {
    isCallCompleted: boolean;
    callClosedReason: CallClosedReason | CallClosedReasonV1;
    callNeedsReschedule?: boolean;
    comments?: string;
    shouldDeleteDocs?: boolean;
  }

  export interface StateProps {
    isExitModalOpen: boolean;
    formValues: FormValues;
  }
  export interface OwnProps {
    roomId: string;
    callId?: string;
  }

  export interface DispatchProps {
    updateExitModalStatus: (isExitModalOpen: boolean) => void;
    addNotifications: (notifications: NotificationItem.PropsWithoutId[]) => object;
  }

  export type Props = InjectedFormProps<FormValues, StateProps & DispatchProps & OwnProps> &
    ExitSessionConfirmationModal.StateProps &
    ExitSessionConfirmationModal.DispatchProps &
    ExitSessionConfirmationModal.OwnProps;
}

export const UnconnectedExitSessionConfirmationModal: React.FC<ExitSessionConfirmationModal.Props> = ({
  isExitModalOpen,
  formValues,
  handleSubmit,
  updateExitModalStatus,
  addNotifications,
  roomId,
  callId,
}) => {
  useDocuments();
  const [inputs, setInputs] = useState<InputConfig[]>([]);
  const isFeedbackCollectionEnabled = getConfig().isFeedbackCollectionEnabled;
  const { document } = useRootSelector(reduxState => reduxState);
  const { documents } = document;
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const terminateRoom = async () => {
    const { isCallCompleted, callClosedReason, callNeedsReschedule, shouldDeleteDocs, comments } = formValues;
    setIsSubmitting(true);
    try {
      if (callId) {
        if (isCallCompleted) {
          await notaryAPI.calls.completeSigningForCall(callId);
        } else {
          if (shouldDeleteDocs) {
            Promise.all(
              documents.map(async doc => {
                doc.roomDocumentId &&
                  (await notaryAPI.documents.deleteRoomDocument(roomId, doc.roomDocumentId, callId));
              }),
            );
          }
          await notaryAPI.calls.stopCall(callId, callClosedReason, callNeedsReschedule, comments);
        }
        // todo: there are two blocks of code that do a redirect when this happens, this is probably broken
      } else {
        noop();
      }
    } catch (err) {
      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'error',
          iconType: 'error',
          content: (err as AxiosError<{ message?: string }>)?.response?.data?.message,
          shouldAutoRemove: true,
        },
      ];
      addNotifications(notifications);
      diagnostics.error(err as Error, {
        additionalMeta: `Error ${isCallCompleted ? 'completing' : 'stopping'} signing`,
      });
      setIsSubmitting(false);
      return;
    }
    if (isCallCompleted && isFeedbackCollectionEnabled) {
      goTo(`/notary/rooms/${roomId}/finish`);
    } else {
      goTo('/notary/journal');
    }
    updateExitModalStatus(false);
  };

  useEffect(() => {
    let inputConfigs: InputConfig[] = [];
    if (getConfig().isCollectRescheduleReasonsEnabled) {
      inputConfigs = Array.from(INPUT_CONFIGS_V1);
    } else {
      inputConfigs = Array.from(INPUT_CONFIGS);
    }
    if (getConfig().allowNotaryToDeleteDocs) {
      inputConfigs.push(shouldDeleteDocsInput);
    }
    const filteredInputs = getFilteredInputs(inputConfigs, formValues);
    setInputs(filteredInputs);
  }, [formValues]);

  return (
    <>
      <ConfirmationModal
        isForm
        isOpen={isExitModalOpen}
        handleClose={() => updateExitModalStatus(false)}
        handleConfirm={handleSubmit(terminateRoom)}
        isDisabled={isSubmitting}
        contentLabel="Exit online notarization"
        heading="Exit your online notarization"
        description={
          'Describe why you want to exit the online notarization and end the signing appointment for everyone.'
        }
        confirmButtonText="Submit"
        closeButtonText="Cancel"
        data-it="ExitSessionConfirmationModal-Notary"
      >
        {/* onSubmit will be handle from the confirmation modal */}
        <TaskForm onSubmit={noop} formTemplate={<TaskInputs inputs={inputs} data-it="TaskInputs" />} />
      </ConfirmationModal>
    </>
  );
};

const mapStateToProps = (state: State): ExitSessionConfirmationModal.StateProps => {
  return {
    isExitModalOpen: state.signingRoom.isExitModalOpen,
    formValues: getFormValues(FORM_NAME)(state) as ExitSessionConfirmationModal.FormValues,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      updateExitModalStatus: updateExitModalStatusAction,
      addNotifications: addNotificationsAction,
    },
    dispatch,
  );

export const ExitSessionConfirmationModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<
    ExitSessionConfirmationModal.FormValues,
    ExitSessionConfirmationModal.StateProps &
      ExitSessionConfirmationModal.DispatchProps &
      ExitSessionConfirmationModal.OwnProps
  >({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    validate: (values: ExitSessionConfirmationModal.FormValues) => {
      const errors: Partial<Record<keyof ExitSessionConfirmationModal.FormValues, string>> = {};
      if (values.callClosedReason === 'OTHER' && (!values.comments || values.comments.trim() === '')) {
        errors.comments = 'Comments are required when selecting "Other reason"';
      }
      return errors;
    },
  })(UnconnectedExitSessionConfirmationModal),
);
